import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Button, Badge, Modal, Form, ListGroup, Alert, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { Group, User, GroupsImportPayload, GroupsImportResponse } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import ActionIconButton from '../components/ActionIconButton';

type SortKey = 'id' | 'name' | 'description' | 'email' | 'members' | 'status';

const Groups: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (
      currentUser?.role === 'admin' ||
      currentUser?.role === 'gerente' ||
      currentUser?.role === 'analista'
    ) {
      fetchGroups();
      fetchUsers();
    }
  }, [currentUser]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groups`, {
        params: { limit: 1000, include_members: true },
      });
      setGroups(response.data);
      
      // Atualizar o grupo selecionado se houver um modal aberto
      if (selectedGroup) {
        const updatedGroup = response.data.find((g: Group) => g.id === selectedGroup.id);
        if (updatedGroup) {
          setSelectedGroup(updatedGroup);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        params: { limit: 10000 },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortBy(key);
      setSortAsc(true);
    }
  };

  const filteredAndSortedGroups = useMemo(() => {
    let list = [...groups];
    if (filterText.trim()) {
      const q = filterText.trim().toLowerCase();
      list = list.filter(
        (g) =>
          (g.name || '').toLowerCase().includes(q) ||
          (g.description || '').toLowerCase().includes(q) ||
          (g.email || '').toLowerCase().includes(q)
      );
    }
    if (filterStatus === 'active') list = list.filter((g) => g.is_active);
    if (filterStatus === 'inactive') list = list.filter((g) => !g.is_active);
    list.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sortBy) {
        case 'id':
          va = a.id;
          vb = b.id;
          break;
        case 'name':
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
          break;
        case 'description':
          va = (a.description || '').toLowerCase();
          vb = (b.description || '').toLowerCase();
          break;
        case 'email':
          va = (a.email || '').toLowerCase();
          vb = (b.email || '').toLowerCase();
          break;
        case 'members':
          va = a.members?.length ?? 0;
          vb = b.members?.length ?? 0;
          break;
        case 'status':
          va = a.is_active ? 1 : 0;
          vb = b.is_active ? 1 : 0;
          break;
        default:
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
      }
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortAsc ? va - vb : vb - va;
      }
      const cmp = String(va).localeCompare(String(vb));
      return sortAsc ? cmp : -cmp;
    });
    return list;
  }, [groups, filterText, filterStatus, sortBy, sortAsc]);

  const handleDeleteGroup = async (group: Group) => {
    const ok = await showConfirm({
      title: 'Excluir grupo',
      message: `Excluir o grupo "${group.name}"? Esta ação não pode ser desfeita.`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setDeletingId(group.id);
    try {
      await axios.delete(`${API_URL}/api/groups/${group.id}`);
      await fetchGroups();
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Erro ao excluir grupo.';
      showAlert({
        title: 'Erro ao excluir grupo',
        message: typeof msg === 'string' ? msg : JSON.stringify(msg),
        variant: 'danger',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleExportGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groups/export`);
      const jsonContent = JSON.stringify(response.data, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `grupos_export_${new Date().toISOString().split('T')[0]}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar grupos:', error);
      showAlert({
        title: 'Erro ao exportar',
        message: 'Erro ao exportar grupos.',
        variant: 'danger',
      });
    }
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  if (
    currentUser?.role !== 'admin' &&
    currentUser?.role !== 'gerente' &&
    currentUser?.role !== 'analista'
  ) {
    return <div>Acesso negado</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Grupos</h2>
        {(currentUser?.role === 'admin' || currentUser?.role === 'gerente') && (
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={handleExportGroups}>
              Exportar Grupos
            </Button>
            <Button variant="outline-secondary" onClick={() => setShowImportModal(true)}>
              Importar Grupos
            </Button>
            <Button variant="success" onClick={() => setShowModal(true)}>
              Novo Grupo
            </Button>
          </div>
        )}
      </div>

      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <InputGroup style={{ maxWidth: 280 }}>
              <Form.Control
                type="text"
                placeholder="Filtrar por nome, descrição ou email..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </InputGroup>
            <Form.Select
              style={{ width: 'auto' }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </Form.Select>
          </div>

          {groups.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum grupo encontrado</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('id')} title="Ordenar por ID">
                    ID {sortBy === 'id' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')} title="Ordenar por Nome">
                    Nome {sortBy === 'name' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('description')} title="Ordenar por Descrição">
                    Descrição {sortBy === 'description' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('email')} title="Ordenar por Email">
                    Email {sortBy === 'email' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('members')} title="Ordenar por Membros">
                    Membros {sortBy === 'members' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')} title="Ordenar por Status">
                    Status {sortBy === 'status' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedGroups.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      Nenhum grupo corresponde ao filtro.
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedGroups.map((group) => (
                    <tr key={group.id}>
                      <td>{group.id}</td>
                      <td>{group.name}</td>
                      <td>{group.description || '-'}</td>
                      <td>{group.email || '-'}</td>
                      <td>{group.members?.length || 0}</td>
                      <td>
                        <Badge bg={group.is_active ? 'success' : 'secondary'}>
                          {group.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td>
                        {(currentUser?.role === 'admin' || currentUser?.role === 'gerente') && (
                          <div className="d-flex flex-wrap gap-1">
                            <ActionIconButton
                              icon="👥"
                              label={`Gerenciar membros do grupo ${group.name}`}
                              variant="outline-primary"
                              onClick={() => {
                                setSelectedGroup(group);
                                setShowMemberModal(true);
                              }}
                            />
                            <ActionIconButton
                              icon="✏️"
                              label={`Editar grupo ${group.name}`}
                              variant="outline-info"
                              onClick={() => {
                                setEditingGroup(group);
                                setShowModal(true);
                              }}
                            />
                            {group.is_user_created ? (
                              <ActionIconButton
                                icon={deletingId === group.id ? '⏳' : '🗑️'}
                                label={`Excluir grupo ${group.name}`}
                                variant="outline-danger"
                                disabled={deletingId === group.id}
                                onClick={() => handleDeleteGroup(group)}
                              />
                            ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {(currentUser?.role === 'admin' || currentUser?.role === 'gerente') && (
        <>
          <GroupModal 
            show={showModal} 
            onHide={() => {
              setShowModal(false);
              setEditingGroup(null);
            }} 
            onSuccess={fetchGroups}
            editingGroup={editingGroup}
          />
          <MemberModal
            show={showMemberModal}
            onHide={() => {
              setShowMemberModal(false);
              setSelectedGroup(null);
            }}
            group={selectedGroup}
            users={users}
            onSuccess={fetchGroups}
          />
          <ImportGroupsModal
            show={showImportModal}
            onHide={() => setShowImportModal(false)}
            onSuccess={fetchGroups}
          />
        </>
      )}
    </div>
  );
};

const GroupModal: React.FC<{
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  editingGroup?: Group | null;
}> = ({ show, onHide, onSuccess, editingGroup }) => {
  const { showAlert } = useModal();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);

  // Preencher campos quando estiver editando
  React.useEffect(() => {
    if (editingGroup) {
      setName(editingGroup.name);
      setDescription(editingGroup.description || '');
      setEmail(editingGroup.email || '');
      setIsActive(editingGroup.is_active);
    } else {
      setName('');
      setDescription('');
      setEmail('');
      setIsActive(true);
    }
  }, [editingGroup, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingGroup) {
        // Atualizar grupo existente
        await axios.put(`${API_URL}/api/groups/${editingGroup.id}`, {
          name,
          description,
          email: email || null,
          is_active: isActive,
        });
      } else {
        // Criar novo grupo
        await axios.post(`${API_URL}/api/groups`, {
          name,
          description,
          email: email || null,
        });
      }
      onSuccess();
      onHide();
      setName('');
      setDescription('');
      setEmail('');
      setIsActive(true);
    } catch (error: any) {
      console.error(`Erro ao ${editingGroup ? 'atualizar' : 'criar'} grupo:`, error);
      showAlert({
        title: 'Erro ao salvar grupo',
        message: error.response?.data?.detail || `Erro ao ${editingGroup ? 'atualizar' : 'criar'} grupo`,
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{editingGroup ? 'Editar Grupo' : 'Novo Grupo'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email do Grupo</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="grupo@exemplo.com"
            />
            <Form.Text className="text-muted">
              Email específico do grupo para receber notificações
            </Form.Text>
          </Form.Group>

          {editingGroup && (
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Grupo Ativo"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading 
              ? (editingGroup ? 'Atualizando...' : 'Criando...') 
              : (editingGroup ? 'Atualizar' : 'Criar')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const MemberModal: React.FC<{
  show: boolean;
  onHide: () => void;
  group: Group | null;
  users: User[];
  onSuccess: () => void;
}> = ({ show, onHide, group, users, onSuccess }) => {
  const { showAlert, showConfirm } = useModal();
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [selectedMemberUser, setSelectedMemberUser] = useState<User | null>(null);
  const [isLeader, setIsLeader] = useState(false);
  const [loading, setLoading] = useState(false);

  const availableUsersForGroup = useMemo(() => {
    if (!group) return [];
    return users.filter((u) => !group.members?.some((m) => m.user_id === u.id));
  }, [group, users]);

  const filteredMemberPickerUsers = useMemo(() => {
    const q = memberSearchTerm.trim().toLowerCase();
    if (!q) return [];
    return availableUsersForGroup
      .filter(
        (u) =>
          (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [availableUsersForGroup, memberSearchTerm]);

  useEffect(() => {
    if (!show) {
      setMemberSearchTerm('');
      setSelectedMemberUser(null);
      setIsLeader(false);
    }
  }, [show]);

  useEffect(() => {
    setMemberSearchTerm('');
    setSelectedMemberUser(null);
  }, [group?.id]);

  const handleAddMember = async () => {
    if (!group || !selectedMemberUser) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/groups/${group.id}/members`, {
        user_id: selectedMemberUser.id,
        is_leader: isLeader,
      });
      onSuccess();
      setMemberSearchTerm('');
      setSelectedMemberUser(null);
      setIsLeader(false);
    } catch (error: any) {
      console.error('Erro ao adicionar membro:', error);
      showAlert({
        title: 'Erro ao adicionar membro',
        message: error.response?.data?.detail || 'Erro ao adicionar membro',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!group) return;

    const ok = await showConfirm({
      title: 'Remover membro',
      message: 'Tem certeza que deseja remover este membro?',
      variant: 'danger',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api/groups/${group.id}/members/${memberId}`);
      onSuccess();
    } catch (error) {
      console.error('Erro ao remover membro:', error);
      showAlert({
        title: 'Erro ao remover membro',
        message: 'Não foi possível remover o membro. Tente novamente.',
        variant: 'danger',
      });
    }
  };

  if (!group) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Membros do Grupo: {group.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Adicionar Membro</Form.Label>
          <div className="d-flex gap-2 align-items-start flex-wrap">
            <div style={{ flex: '1 1 280px', position: 'relative', minWidth: 0 }}>
              <Form.Control
                type="text"
                autoComplete="off"
                placeholder="Digite nome ou e-mail para buscar..."
                value={
                  selectedMemberUser
                    ? `${selectedMemberUser.name} (${selectedMemberUser.email})`
                    : memberSearchTerm
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setMemberSearchTerm(v);
                  setSelectedMemberUser(null);
                }}
              />
              {memberSearchTerm.trim() && !selectedMemberUser && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1056,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {filteredMemberPickerUsers.length === 0 ? (
                    <div className="px-3 py-2 text-muted small">Nenhum usuário encontrado</div>
                  ) : (
                    filteredMemberPickerUsers.map((user) => (
                      <div
                        key={user.id}
                        role="button"
                        tabIndex={0}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                        }}
                        onClick={() => {
                          setSelectedMemberUser(user);
                          setMemberSearchTerm('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedMemberUser(user);
                            setMemberSearchTerm('');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        {user.name} ({user.email})
                      </div>
                    ))
                  )}
                </div>
              )}
              {selectedMemberUser && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setSelectedMemberUser(null);
                    setMemberSearchTerm('');
                  }}
                  style={{
                    position: 'absolute',
                    right: '4px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '0 6px',
                    lineHeight: 1,
                  }}
                  aria-label="Limpar seleção"
                >
                  ✕
                </Button>
              )}
            </div>
            <Form.Check
              type="checkbox"
              label="Líder"
              checked={isLeader}
              onChange={(e) => setIsLeader(e.target.checked)}
              className="ms-2 align-self-center"
            />
            <Button
              variant="success"
              onClick={handleAddMember}
              disabled={!selectedMemberUser || loading}
              className="align-self-center"
            >
              Adicionar
            </Button>
          </div>
        </Form.Group>

        <ListGroup>
          {group.members && group.members.length > 0 ? (
            group.members.map((member) => (
              <ListGroup.Item key={member.id} className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{member.user_name || 'N/A'}</strong>
                  {member.is_leader && <Badge bg="warning" className="ms-2">Líder</Badge>}
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveMember(member.id)}
                >
                  Remover
                </Button>
              </ListGroup.Item>
            ))
          ) : (
            <ListGroup.Item>Nenhum membro</ListGroup.Item>
          )}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ImportGroupsModal: React.FC<{
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}> = ({ show, onHide, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [importResult, setImportResult] = useState<GroupsImportResponse | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const ext = selectedFile.name.toLowerCase().split('.').pop() || '';
      if (ext !== 'json') {
        setError('Por favor, selecione um arquivo JSON.');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess('');
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Por favor, selecione um arquivo JSON.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const text = await file.text();
      const payload = JSON.parse(text) as GroupsImportPayload;
      if (!payload || !Array.isArray(payload.groups)) {
        throw new Error('Arquivo inválido: esperado objeto com campo "groups".');
      }

      const response = await axios.post(
        `${API_URL}/api/groups/import`,
        payload,
        {
          params: { dry_run: dryRun },
        }
      );
      const result = response.data as GroupsImportResponse;
      setImportResult(result);

      if (result.errors > 0) {
        setError(`Importação finalizada com ${result.errors} erro(s).`);
      }
      setSuccess(
        dryRun
          ? `Validação concluída: ${result.created} criação(ões), ${result.updated} atualização(ões).`
          : `Importação concluída: ${result.created} criado(s), ${result.updated} atualizado(s).`
      );
      if (!dryRun) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Erro ao importar grupos:', error);
      setError(error.response?.data?.detail || error.message || 'Erro ao processar o arquivo JSON.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setDryRun(true);
    setError('');
    setSuccess('');
    setImportResult(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Importar Grupos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Selecione um arquivo JSON</Form.Label>
          <Form.Control
            type="file"
            accept=".json"
            onChange={handleFileChange}
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Utilize o arquivo gerado por "Exportar Grupos".
          </Form.Text>
        </Form.Group>
        <Form.Check
          type="switch"
          id="groups-dry-run"
          label="Executar validação sem gravar (dry_run)"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
          disabled={loading}
          className="mb-3"
        />

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mt-3">
            {success}
          </Alert>
        )}

        {file && (
          <div className="mt-3">
            <p><strong>Arquivo selecionado:</strong> {file.name}</p>
          </div>
        )}
        {importResult && (
          <div className="mt-3">
            <p className="mb-2">
              <strong>Resumo:</strong> {importResult.created} criado(s), {importResult.updated} atualizado(s), {importResult.errors} erro(s).
            </p>
            <div style={{ maxHeight: 180, overflow: 'auto' }}>
              <Table size="sm" bordered>
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Ação</th>
                    <th>Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.results.map((item, idx) => (
                    <tr key={`${item.name}-${idx}`}>
                      <td>{item.name}</td>
                      <td>{item.action}</td>
                      <td>{item.message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleImport} 
          disabled={!file || loading}
        >
          {loading ? 'Importando...' : 'Importar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Groups;



